/*
 * Tile-source configuration for MapLibre (ADR-0010, corrected 2026-06-03 — the
 * default flipped from `sjokartraster` to `topo` because topo reads better at the
 * default zoom around the cabin).
 *
 *  - Default basemap is Kartverket's public WMTS raster cache, layer `topo` (the
 *    topographic land map). The mode toggle swaps the {layer} portion of the URL
 *    between `topo` and `sjokartraster`. A Flyfoto third segment is a deferred
 *    post-MVP mode (the Norge-i-bilder service has its own terms + endpoint) —
 *    the segment ships as visible-but-disabled per the wireframe.
 *  - OSM is the fallback used after repeated Kartverket tile-load errors;
 *    "the map still works if Kartverket is down."
 */

export type MapMode = 'sjokart' | 'topo';
// Flyfoto is a deferred third mode — present in the UI as a disabled "snart"
// segment; not in MapMode because the renderer can't switch to it.

export const KARTVERKET_TILE_URL = (mode: MapMode) =>
  `https://cache.kartverket.no/v1/wmts/1.0.0/${mode === 'sjokart' ? 'sjokartraster' : 'topo'}/default/webmercator/{z}/{y}/{x}.png`;

export const OSM_TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';

// Hesnesøya 47, 4885 Grimstad — the cabin (project fact). Used as the map's
// initial centre + the always-visible cabin pin position.
export const CABIN_LONLAT: [number, number] = [8.654351150960515, 58.33859005682095];

// How many consecutive tile-load errors from the basemap source trip the OSM
// fallback. The original 8 was too tight in practice: a single deep-zoom pan
// over a data-sparse Kartverket area piled up enough 404s to trip the fallback
// permanently. Raised to 24 so only a sustained outage tips it; combined with
// the maxZoom cap below, 24 also stays well clear of normal-use noise.
export const FALLBACK_ERROR_THRESHOLD = 24;

// Kartverket's public raster cache nominally goes to z=18, but coverage thins
// near the cap — at z=18 over many areas, several tiles in view return 404 and
// the fallback would trip. Cap MapLibre's camera at z=17 (combined with the
// source-side maxzoom below, MapLibre over-samples the z=17 tile beyond it
// silently rather than fetching nonexistent tiles). GATE-1 defect 2.
export const MAP_MAX_ZOOM = 17;
export const MAP_MIN_ZOOM = 4;

// Source-side maxzoom telling MapLibre "tiles only exist up to this level; if
// you need more zoom, upsample the z=N tile rather than fetching z=N+1." This
// is independent of the camera cap above — the two together mean (a) the
// camera can't go past MAP_MAX_ZOOM anyway, and (b) any internal request for
// a tile past this number is upsampled in-process instead of going to the
// network. Belt and braces against the runaway-fallback failure mode.
export const SOURCE_MAX_ZOOM = 17;
